// FILE: src/pages/C2BVarianceReportPage.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  CircularProgress,
  Alert,
  Stack,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import API from "../api"; // Centralized Axios instance
import { formatDate } from "../utils/formatDate";

function C2BVarianceReportPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Filters and report states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [accounts, setAccounts] = useState([]);

  // Raw data including pagination
  const [rawReportData, setRawReportData] = useState({
    rows: [],
    totals: {},
    pagination: { currentPage: 1, totalPages: 1 },
    currency: "USD",
  });

  // Client-side filtered view
  const [reportData, setReportData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Client-side filters
  const [filterC2BVarianceClass, setFilterC2BVarianceClass] = useState("ALL");
  const [tillUserFilter, setTillUserFilter] = useState("ALL");
  const [shiftManagerFilter, setShiftManagerFilter] = useState("ALL");

  // Pagination state
  const [page, setPage] = useState(1);

  // Default date range (current month)
  const defaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  // Initialize date filters
  useEffect(() => {
    const { start, end } = defaultDateRange();
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Fetch accounts dropdown
  useEffect(() => {
    API.get("/accounts", { params: { limit: 1000000 } })
      .then(res => setAccounts(res.data.accounts || []))
      .catch(err => {
        console.error("Error fetching accounts:", err);
        setError(err.response?.data?.message || "Error fetching accounts");
      });
  }, []);

  // Fetch or append report data
  const generateReport = async (pageNumber = 1, append = false) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        startDate,
        endDate,
        account: accountFilter,
        tillUser: tillUserFilter,
        shiftManager: shiftManagerFilter,
        page: pageNumber,
        limit: 50,
      };
      const res = await API.get("/advance-reports/c2b-variance", { params });
      setRawReportData(prev =>
        append
          ? {
              rows: [...prev.rows, ...res.data.rows],
              totals: res.data.totals,
              pagination: res.data.pagination,
              currency: res.data.currency,
            }
          : {
              rows: res.data.rows,
              totals: res.data.totals,
              pagination: res.data.pagination,
              currency: res.data.currency,
            }
      );
      setPage(res.data.pagination.currentPage);
    } catch (err) {
      console.error("Error generating report:", err);
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => generateReport(1);
  const handleLoadMore = () => {
    if (page < rawReportData.pagination.totalPages) {
      generateReport(page + 1, true);
    }
  };

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const queryParams = `?startDate=${startDate}&endDate=${endDate}&account=${accountFilter}&tillUser=${tillUserFilter}&shiftManager=${shiftManagerFilter}`;
      const response = await API.get(
        `/advance-reports/c2b-variance/export${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const csvBlob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "c2b-variance-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      setError(err.response?.data?.message || "Error exporting CSV");
    }
  };

  // Apply client-side variance filter and recalc totals
  useEffect(() => {
    let filtered = rawReportData.rows;
    if (filterC2BVarianceClass !== "ALL") {
      filtered = filtered.filter(row => {
        const val = Number(row.c2bVariance);
        if (filterC2BVarianceClass === "Surplus") return val > 0;
        if (filterC2BVarianceClass === "Deficit") return val < 0;
        return val === 0;
      });
    }
    const totals = filtered.reduce(
      (acc, row) => {
        acc.totalExpected += Number(row.expectedC2B) || 0;
        acc.totalActual += Number(row.actualC2B) || 0;
        acc.totalVariance += Number(row.c2bVariance) || 0;
        return acc;
      },
      { totalExpected: 0, totalActual: 0, totalVariance: 0 }
    );
    setReportData({ rows: filtered, totals });
  }, [rawReportData, filterC2BVarianceClass]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        C2B Variance Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={accounts}
            getOptionLabel={opt => `${opt.accountName} (${opt.accountNo})`}
            value={
              accountFilter === "ALL"
                ? { _id: "ALL", accountName: "All Accounts" }
                : accounts.find(a => a._id === accountFilter) || null
            }
            onChange={(e, nv) => setAccountFilter(nv ? nv._id : "ALL")}
            renderInput={params => <TextField {...params} label="Account" />}
            sx={{ width: 250 }}
            ListboxProps={{ style: { maxHeight: 240 } }}
          />
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption">
            Date Range: {startDate} to {endDate}
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <TextField
            label="Till User"
            value={tillUserFilter}
            onChange={e => setTillUserFilter(e.target.value)}
            helperText='Type "ALL" to remove filter'
          />
          <TextField
            label="Shift Manager"
            value={shiftManagerFilter}
            onChange={e => setShiftManagerFilter(e.target.value)}
            helperText='Type "ALL" to remove filter'
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="variance-class-label">C2B Variance</InputLabel>
            <Select
              labelId="variance-class-label"
              value={filterC2BVarianceClass}
              label="C2B Variance"
              onChange={e => setFilterC2BVarianceClass(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="Surplus">Surplus</MenuItem>
              <MenuItem value="Deficit">Deficit</MenuItem>
              <MenuItem value="Zero">Zero</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleGenerateReport}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={handleExportCSV} sx={{ ml: { sm: 2 } }}>
            Export to CSV
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Till Category</TableCell>
                  <TableCell>Expected C2B</TableCell>
                  <TableCell>Actual C2B</TableCell>
                  <TableCell>Actual C2B Date</TableCell>
                  <TableCell>C2B Variance</TableCell>
                  <TableCell>Shift Manager</TableCell>
                  <TableCell>Till Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>
                      {row.accountName}
                      {row.accountNo ? ` (${row.accountNo})` : ""}
                    </TableCell>
                    <TableCell>{row.tillCategory}</TableCell>
                    <TableCell>{(Number(row.expectedC2B) || 0).toLocaleString()}</TableCell>
                    <TableCell>{(Number(row.actualC2B) || 0).toLocaleString()}</TableCell>
                    <TableCell>{formatDate(row.actualC2BDate)}</TableCell>
                    <TableCell>{(Number(row.c2bVariance) || 0).toLocaleString()}</TableCell>
                    <TableCell>{row.shiftManager}</TableCell>
                    <TableCell>{row.tillUsers}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    Totals:
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {(reportData.totals.totalExpected || 0).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {(reportData.totals.totalActual || 0).toLocaleString()}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {(reportData.totals.totalVariance || 0).toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>

          {/* Load More Button */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            {page < rawReportData.pagination.totalPages && (
              <Button variant="contained" onClick={handleLoadMore} disabled={loading}>
                {loading ? "Loading…" : "Load More"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default C2BVarianceReportPage;
